import React from 'react';
import { TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';

import classNames from '@utils/classnames';
import { formatStatCardDelta, getStatCardDeltaTone } from './StatCard.utils';

export type { StatCardDeltaTone } from './StatCard.utils';
export { formatStatCardDelta, getStatCardDeltaTone } from './StatCard.utils';

export type StatCardVariant = 'default' | 'compact';

const statCardVariants = cva('box-border min-w-0 rounded-lg border border-border bg-surface', {
  variants: {
    variant: {
      default: 'flex flex-col items-start justify-start gap-1.5 p-3.5',
      compact: 'flex flex-row items-center gap-3 px-3.5 py-2.5',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const statCardValueVariants = cva('whitespace-nowrap text-left font-semibold tracking-tight', {
  variants: {
    variant: {
      default: 'text-2xl',
      compact: 'text-lg',
    },
    tone: {
      default: 'text-fg',
      positive: 'text-positive-text',
      negative: 'text-negative-text',
    },
  },
  defaultVariants: {
    variant: 'default',
    tone: 'default',
  },
});

export type StatCardTone = NonNullable<VariantProps<typeof statCardValueVariants>['tone']>;

export interface StatCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  unit?: React.ReactNode;
  icon?: React.ReactNode;
  delta?: number | string;
  /** Extra line under the value (default) or right-aligned text (compact). */
  subValue?: React.ReactNode;
  /** Short note next to the delta, e.g. "vs. vorige". */
  deltaNote?: React.ReactNode;
  tone?: StatCardTone;
  variant?: StatCardVariant;
  className?: string;
}

function renderSizedIcon(icon: React.ReactNode, sizeClassName: string) {
  if (React.isValidElement<{ className?: string }>(icon)) {
    return React.cloneElement(icon, {
      className: classNames(sizeClassName, 'shrink-0', icon.props.className),
    });
  }

  return icon;
}

const IconTile: React.FC<{ icon: React.ReactNode }> = ({ icon }) => {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-neutral-subtle text-fg-secondary">
      {renderSizedIcon(icon, 'h-4 w-4')}
    </div>
  );
};

const ValueRow: React.FC<{
  value: React.ReactNode;
  unit?: React.ReactNode;
  variant: StatCardVariant;
  tone: StatCardTone;
}> = ({ value, unit, variant, tone }) => {
  return (
    <div className="flex w-fit shrink-0 items-baseline gap-1">
      <div className={statCardValueVariants({ variant, tone })}>{value}</div>
      {unit != null && unit !== '' && (
        <div
          className={classNames(
            'whitespace-nowrap font-medium text-fg-muted',
            variant === 'compact' ? 'text-2xs' : 'text-xs',
          )}
        >
          {unit}
        </div>
      )}
    </div>
  );
};

const Delta: React.FC<{ delta: number | string; note?: React.ReactNode }> = ({ delta, note }) => {
  const deltaTone = getStatCardDeltaTone(delta);
  const label = typeof delta === 'number' ? formatStatCardDelta(delta) : delta;
  const TrendIcon = deltaTone === 'negative' ? TrendDownIcon : TrendUpIcon;

  return (
    <div className="flex items-center gap-1 whitespace-nowrap text-xs">
      <span
        className={classNames('inline-flex items-center gap-0.5 font-semibold', {
          'text-positive-text': deltaTone === 'positive',
          'text-negative-text': deltaTone === 'negative',
          'text-fg-muted': deltaTone === 'neutral',
        })}
      >
        {deltaTone !== 'neutral' && <TrendIcon className="h-3 w-3 shrink-0" weight="bold" />}
        {label}
      </span>
      {note != null && <span className="text-2xs text-fg-muted">{note}</span>}
    </div>
  );
};

/** KPI tile: caption, big number, change versus the previous period. One shape for the whole product. */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  unit,
  icon,
  delta,
  subValue,
  deltaNote,
  tone = 'default',
  variant = 'default',
  className,
}) => {
  if (variant === 'compact') {
    return (
      <div className={classNames(statCardVariants({ variant }), className)}>
        {icon != null && <IconTile icon={icon} />}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
          <div className="max-w-full truncate text-xs font-medium text-fg-muted">{label}</div>
          <ValueRow value={value} unit={unit} variant="compact" tone={tone} />
        </div>
        {delta != null ? (
          <Delta delta={delta} />
        ) : subValue != null ? (
          <div className="shrink-0 whitespace-nowrap text-xs font-medium text-fg-muted">{subValue}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={classNames(statCardVariants({ variant }), className)}>
      <div className="flex w-full items-center justify-between gap-2">
        <div className="min-w-0 truncate text-xs font-medium text-fg-muted">{label}</div>
        {icon != null && <IconTile icon={icon} />}
      </div>
      <ValueRow value={value} unit={unit} variant="default" tone={tone} />
      {delta != null && <Delta delta={delta} note={deltaNote} />}
      {subValue != null && <div className="text-xs text-fg-muted">{subValue}</div>}
    </div>
  );
};
